
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { FileText, Edit3, Save, X, Plus } from "lucide-react";
import { Candidate } from "../types";
import { useUpdateCandidateProfile } from "@/hooks/mutations/useUpdateCandidateProfile"; // Corrected path
import { toast } from "sonner";

export interface NotesProps {
  candidate: Candidate;
}

export const Notes = ({ candidate }: NotesProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedNotes, setEditedNotes] = useState(candidate.notes || "");
  const { updateCandidateProfile, isUpdatingProfile } = useUpdateCandidateProfile();

  const handleEdit = () => {
    setEditedNotes(candidate.notes || "");
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedNotes(candidate.notes || ""); // Reset to original notes
  };

  const handleSave = () => {
    updateCandidateProfile(
      { candidateId: candidate.id, data: { notes: editedNotes } },
      {
        onSuccess: (updatedCandidate) => {
          setIsEditing(false);
          if (updatedCandidate?.notes !== undefined) {
            setEditedNotes(updatedCandidate.notes);
          }
          // Toast is handled by the hook
        },
        onError: () => {
          // Toast is handled by the hook
        }
      }
    );
  };

  if (!candidate && !isEditing) return null; // Should not happen if candidate prop is always passed

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-xl">
          <FileText className="h-5 w-5 text-primary" />
          Notes
        </CardTitle>
        {!isEditing && (
          <Button variant="outline" size="sm" onClick={handleEdit}>
            {candidate.notes ? <Edit3 className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            {candidate.notes ? "Edit Notes" : "Add Notes"}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-4">
            <Textarea
              placeholder="Enter notes here..."
              value={editedNotes}
              onChange={(e) => setEditedNotes(e.target.value)}
              className="min-h-[150px]"
              disabled={isUpdatingProfile}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={handleCancel} disabled={isUpdatingProfile}>
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={isUpdatingProfile}>
                <Save className="h-4 w-4 mr-1" /> {isUpdatingProfile ? "Saving..." : "Save Notes"}
              </Button>
            </div>
          </div>
        ) : (
          candidate.notes ? (
            <p className="whitespace-pre-wrap text-muted-foreground">{candidate.notes}</p>
          ) : (
            <p className="text-muted-foreground italic">No notes added yet.</p>
          )
        )}
      </CardContent>
    </Card>
  );
};
